import { chronicleIsRecordingAtom } from '@/atoms/chronicle';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { DatadogDurationVital } from '@/components/constants/datadog-vitals';
import { ability, ACTIONS, PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useCanCreateSession } from '@/hooks/useCanCreateSession';
import { useNewSession } from '@/hooks/useNewSession';
import { useNewSessionBlockers } from '@/hooks/useNewSessionBlockers';
import { useNewSessionDelegation } from '@/hooks/useShowClinicianSelector';
import { type SessionCreatedEntryPoint } from '@/models/analytics/features/sessions';
import { datadogRum } from '@datadog/browser-rum';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

/**
 * The single decision every "create a new session" entry point consumes.
 *
 * A discriminated union so a call site handles exactly one shape and TypeScript
 * exhaustiveness makes forgetting the clinician-picker mode impossible — the
 * drift that left the sidebar "+" hard-disabled for assistants.
 */
export type NewSessionAction =
  | { mode: 'hidden' }
  | {
      mode: 'directCreate';
      isDisabled: boolean;
      isPending: boolean;
      /**
       * True when the user lacks self-create permission and isn't reviewing
       * another clinician — the caller shows the "first select clinicians…"
       * hint. The decision lives here; the copy is resolved by the view.
       */
      promptSelectClinicians: boolean;
      /** Owns the perf duration-vital + tracking preamble, then self-creates. */
      onCreate: () => void;
    }
  | {
      mode: 'clinicianPicker';
      isDisabled: boolean;
      isPending: boolean;
      /** Owns the perf preamble, then creates on behalf of the picked member. */
      onCreateForMember: (memberId: string) => void;
    };

/**
 * Derives the new-session action for one entry point from the same two decision
 * hooks every other surface uses — `useCanCreateSession` (self-create) and
 * `useNewSessionDelegation` (create-on-behalf-of) — plus the hard blocks
 * (recording timer, offline capture, chronicle, pending, integration
 * patient-lock) and the EHR-launch hide. Callers render per mode and chain their own side effects (tutorial
 * advance, mobile-sheet close) after `onCreate`/`onCreateForMember`.
 */
export const useNewSessionAction = (
  entryPoint: SessionCreatedEntryPoint
): NewSessionAction => {
  const isEhrLaunch = useIsEhrLaunch();
  const { createNewSession, createNewSessionForMember, isPending, isReady } =
    useNewSession({ entryPoint });
  // Mirror of `useIsReviewingOtherUser`, sourced through the `@heidi/workspace`
  // door rather than the legacy `src/hooks/query/useQueryUser` hub — a new file
  // reaching into that hub trips the workspace/identity-projection ratchet, and
  // the derivation can't live in the headless package because it reads the app
  // atom `selectedReviewMemberAtom`.
  const selectedReviewMember = useAtomValue(selectedReviewMemberAtom);
  const { data: userData } = useQueryUser();
  const isReviewingOtherUser = Boolean(
    selectedReviewMember && userData && selectedReviewMember !== userData.kinde_user_id
  );
  const canCreateSession = useCanCreateSession(isReviewingOtherUser);
  // Admins with the submenu self-create directly like any other
  // clinician — only the assistant/no-self-create-permission picker routes
  // this hook into `clinicianPicker` mode.
  const showClinicianSelector = useNewSessionDelegation() === 'assistantPicker';
  const isChronicleRecording = useAtomValue(chronicleIsRecordingAtom);
  const { isRecordingTimerRunning, isOfflineCaptureActive, isIntegrationPatientLocked } =
    useNewSessionBlockers();

  // The vital starts only for a create that was not refused: a refusal never
  // reaches the page that stops it.
  const onCreate = useCallback(() => {
    if (createNewSession({ metadata: { entryPoint } })) return;
    datadogRum.startDurationVital(DatadogDurationVital.NEW_SESSION);
  }, [entryPoint, createNewSession]);

  const onCreateForMember = useCallback(
    (memberId: string) => {
      if (createNewSessionForMember(memberId, { metadata: { entryPoint } })) return;
      datadogRum.startDurationVital(DatadogDurationVital.NEW_SESSION);
    },
    [entryPoint, createNewSessionForMember]
  );

  // Every hard block that greys out a session-create surface. The picker mode
  // drops `!canCreateSession` / `!isReady` (see below) but keeps these.
  const isBlocked =
    isRecordingTimerRunning ||
    isOfflineCaptureActive ||
    isChronicleRecording ||
    isIntegrationPatientLocked;

  return useMemo<NewSessionAction>(() => {
    if (isEhrLaunch) {
      return { mode: 'hidden' };
    }

    if (showClinicianSelector) {
      // Creating on behalf of another clinician doesn't need self
      // SESSION_CREATE (self is filtered out of the list when absent), and the
      // picker only ever shows on the new list where `isReady` is definitionally
      // true — so both drop out here, leaving only the hard blocks.
      return {
        mode: 'clinicianPicker',
        isDisabled: isPending || isBlocked,
        isPending,
        onCreateForMember,
      };
    }

    const promptSelectClinicians =
      !isReviewingOtherUser &&
      !ability.can(ACTIONS.ACCESS, PERMISSION_SUBJECTS.SESSION_CREATE);

    return {
      mode: 'directCreate',
      isDisabled: !isReady || isPending || !canCreateSession || isBlocked,
      isPending,
      promptSelectClinicians,
      onCreate,
    };
  }, [
    isEhrLaunch,
    showClinicianSelector,
    isReviewingOtherUser,
    canCreateSession,
    isReady,
    isPending,
    isBlocked,
    onCreate,
    onCreateForMember,
  ]);
};
